'use client';

import { ChevronDown, Menu, X } from 'lucide-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { SupportedLocale } from '@/lib/translation/types';
import { LanguageSwitcher } from '../../language-switcher';
import type { NavbarStaticContent } from './static-content';

// Define interface for nav links
interface NavLink {
  label: string;
  href?: string;
  hasSubmenu?: boolean;
  onClick?: () => void;
  target?: string;
  rel?: string;
}
const DepartmentMenu = dynamic(() => import('@/components/cms/navbar/department-menu'), {
  loading: () => null
});
const TreatmentSearch = dynamic(() => import('@/components/cms/navbar/treatment-search'), {
  loading: () => null
});
interface NavbarProps {
  defaultLocale: SupportedLocale;
  staticContent: Awaited<ReturnType<typeof NavbarStaticContent>>;
}
export function Navbar({
  defaultLocale,
  staticContent
}: NavbarProps) {
  const t = useTranslations('Navbar');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollY = useRef(0);
  const pathname = usePathname();
  useEffect(() => {
    // Check scroll position after hydration
    if (window.scrollY > 50) {
      setIsScrolled(true);
    }

    // Initialize lastScrollY ref with current scroll position
    lastScrollY.current = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // When scrolling down, make the navbar opaque sooner (at 50px)
      if (currentScrollY > 50) {
        setIsScrolled(true);
      }
      // When scrolling up, make the navbar transparent only when near the top (20px)
      // This prevents flickering when scrolling up from a position far down the page
      else if (currentScrollY <= 20) {
        setIsScrolled(false);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    // NOTE: This effect resets the search state when the pathname changes.
    setIsMenuOpen(false);
    setIsSubmenuOpen(false);
  }, [pathname]);

  // Mark component as hydrated
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  const handleMouseEnter = useCallback(() => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    setIsSubmenuOpen(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    submenuTimeoutRef.current = setTimeout(() => {
      setIsSubmenuOpen(false);
    }, 300);
  }, []);
  const toggleSubmenu = useCallback(() => {
    setIsSubmenuOpen(prev => !prev);
  }, []);
  const navLinks: NavLink[] = [{
    label: t('treatments'),
    hasSubmenu: true
  }, {
    href: '/science',
    label: t('science')
  }, {
    href: '/clinic',
    label: t('theClinic')
  }, {
    href: defaultLocale === 'en' ? 'https://shop.istitutoimage.com' : 'https://shop.istitutoimage.it',
    label: t('store')
  }];
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out ${isScrolled || isSubmenuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`} data-sentry-component="Navbar" data-sentry-source-file="navbar.tsx">
      <div className="container mx-auto z-50">
        <div className="flex justify-between items-center py-4 md:py-6">
          <Link href="/" className="flex items-center text-black text-sm" data-sentry-element="Link" data-sentry-source-file="navbar.tsx">
            <Image src="/svg/image_blk.svg" alt={staticContent.logoAlt} width={111} height={37} data-sentry-element="Image" data-sentry-source-file="navbar.tsx" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link, index) => <div key={index}>
                {link.hasSubmenu ? <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    <button className="text-black text-sm font-medium hover:text-gray-600 flex items-center h-full" onClick={e => {
                e.preventDefault();
                // Focus the search input after a small delay to ensure the dropdown has rendered
                setTimeout(() => {
                  const searchInput = document.querySelector('.treatment-search-input');
                  if (searchInput instanceof HTMLInputElement) {
                    searchInput.focus();
                  }
                }, 100);
              }}>
                      {link.label}
                      <ChevronDown className={`ml-1 transition-transform duration-300 ${isSubmenuOpen ? 'rotate-180' : ''}`} size={16} />
                    </button>
                    {isSubmenuOpen && <div className="absolute w-full h-8 bottom-0 translate-y-full" />}
                  </div> : <Link href={link.href || ''} className="text-black text-sm font-medium hover:text-gray-600 flex items-center h-full" {...link.target && {
              target: link.target
            }} {...link.rel && {
              rel: link.rel
            }}>
                    {link.label}
                  </Link>}
              </div>)}
          </div>

          <div className="flex items-center gap-4">
            <LanguageSwitcher className="hidden lg:block" defaultLocale={defaultLocale} data-sentry-element="LanguageSwitcher" data-sentry-source-file="navbar.tsx" />
            <Link href="/book" data-sentry-element="Link" data-sentry-source-file="navbar.tsx">
              <Button className="hidden md:block font-medium px-4 py-2 bg-black text-white hover:bg-offblue text-xs hover:cursor-pointer" data-sentry-element="Button" data-sentry-source-file="navbar.tsx">
                {staticContent.bookNowText}
              </Button>
            </Link>
            <button className="md:hidden text-black" onClick={() => setIsMenuOpen(true)}>
              <Menu className="text-black" data-sentry-element="Menu" data-sentry-source-file="navbar.tsx" />
            </button>
          </div>
        </div>
      </div>

      {/* Treatments Submenu */}
      {isHydrated && <div className={`
            absolute left-0 right-0 
            bg-white shadow-lg
            transition-all duration-300 ease-in-out
            transform
            ${isSubmenuOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none translate-y-[-8px]'}
          `} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {isSubmenuOpen && <div className="container mx-auto pb-8">
              <TreatmentSearch locale={defaultLocale} />
              <DepartmentMenu />
            </div>}
        </div>}

      {/* Mobile Navigation */}
      {isHydrated && <div className={`
            md:hidden fixed inset-0 bg-white z-50 transition-all ease-in-out
            ${isMenuOpen ? 'translate-y-0 opacity-100 pointer-events-auto' : '-translate-y-5 opacity-0 pointer-events-none'}
          `}>
          <div className="flex flex-col items-center justify-center p-0 h-full overflow-y-auto">
            {/* Header */}
            <div className="flex-none w-full flex justify-between items-center px-4">
              <LanguageSwitcher defaultLocale={defaultLocale} />
              <button className="text-black" onClick={() => setIsMenuOpen(false)}>
                <X size={24} />
              </button>
            </div>

            {/* Navigation Links */}
            <div className="w-full flex-1 overflow-hidden relative">
              <ScrollArea className="h-full">
                {/* Treatments Section with Search */}
                <div className="py-4 ">
                  <TreatmentSearch locale={defaultLocale} />
                  <div className="w-full h-px bg-black " />
                  <DepartmentMenu />
                </div>

                {/* Regular Navigation Links */}
                {navLinks.filter(link => !link.hasSubmenu) // Skip the treatments link as it's handled above
            .map((link, index) => <div key={index}>
                      {/* Divider */}
                      <div className="w-full h-px bg-black " />

                      {/* Link */}
                      <Link href={link.href || ''} className="block w-full py-4" {...link.target && {
                target: link.target
              }} {...link.rel && {
                rel: link.rel
              }}>
                        <h3 className="text-sm font-nudica font-medium uppercase px-4">{link.label}</h3>
                      </Link>
                    </div>)}

                {/* Add bottom padding to ensure last item can scroll above gradient */}
                <div className="pb-16"></div>

                {/* Gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-linear-to-t from-white to-transparent pointer-events-none" />
              </ScrollArea>
            </div>

            {/* Book Now Button */}
            <Link href="/book" className="w-full mt-auto flex-none pt-2 px-4 mb-4">
              <Button className="mt-3 w-full font-medium px-6 py-6 bg-offblack text-white hover:bg-offblue text-base hover:cursor-pointer">
                {staticContent.bookNowText}
              </Button>
            </Link>
          </div>
        </div>}
    </nav>;
}